import React, {useMemo} from 'react';
import {applyThemeToDom} from '@ui/themes/utils/apply-theme-to-dom';
import {
  ThemeId,
  ThemeSelectorContext,
  ThemeSelectorContextValue,
} from '@ui/themes/theme-selector-context';
import {CssTheme} from '@ui/themes/css-theme';
import {useSettings} from '@ui/settings/use-settings';
import {useCookie} from '@ui/utils/hooks/use-cookie';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';

const STORAGE_KEY = 'be-active-theme';

interface ThemeProviderProps {
  children: any;
}
export function ThemeProvider({children}: ThemeProviderProps) {
  const {themes} = useSettings();
  const canChangeTheme = themes?.user_change;
  const data = useBootstrapDataStore(s => s.data);
  const allThemes = useMemo(() => data.themes.all || [], [data.themes.all]);
  const initialThemeId = data.themes.selectedThemeId || undefined;
  const userThemeId = (data.user as {theme_id?: number | string | null} | null)
    ?.theme_id;
  const isAppearancePreview =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('appearanceEditor') ===
      'true';

  const [selectedThemeId, setSelectedThemeId] = useCookie(
    STORAGE_KEY,
    `${initialThemeId}`,
  );

  const effectiveThemeId = isAppearancePreview
    ? data.themes.selectedThemeId
    : userThemeId
      ? data.themes.selectedThemeId
      : canChangeTheme
        ? selectedThemeId || data.themes.selectedThemeId
        : data.themes.selectedThemeId || themes?.default_id;

  let selectedTheme = allThemes.find(t => t.id == effectiveThemeId);
  if (!selectedTheme) {
    selectedTheme = allThemes[0];
  }

  const contextValue: ThemeSelectorContextValue = useMemo(() => {
    return {
      allThemes,
      selectedTheme: selectedTheme!,
      selectTheme: (id: ThemeId) => {
        if (!canChangeTheme && !isAppearancePreview) return;
        const theme = findTheme(allThemes, id);
        if (theme) {
          if (!isAppearancePreview) {
            setSelectedThemeId(`${theme.id}`);
          }
          applyThemeToDom(theme);
        }
      },
    };
  }, [
    allThemes,
    selectedTheme,
    setSelectedThemeId,
    canChangeTheme,
    isAppearancePreview,
  ]);

  return (
    <ThemeSelectorContext.Provider value={contextValue}>
      {children}
    </ThemeSelectorContext.Provider>
  );
}

function findTheme(themes: CssTheme[], id: ThemeId) {
  return themes.find(t => {
    if (id === 'light') {
      return t.default_light === true;
    }
    if (id === 'dark') {
      return t.default_dark === true;
    }
    return t.id === id;
  });
}
